import React, { FC, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import useCloneForm from '../../../hooks/api/form/useCloneForm';
import ModalComponent from '../../../components/modal/ModalComponent';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import * as Yup from 'yup';
import { Formik, Form, FormikProps, FastField } from 'formik';
import CultureTextInput from '../../../components/culture/CultureTextInput';
import { CultureTextModel } from '../../../models/form/form';
import FieldWrapper from '../../../components/library/inputs/fieldWrapper/FieldWrapper';
import { cultureYup } from '../../../utilities/CultureUtilities';

interface Props {
  open: boolean;
  close: (cloned: boolean) => void;
  formId: string;
  formName: CultureTextModel[] | string;
  localizations: string[];
}

interface Values {
  newFormName: CultureTextModel[] | string;
}

const CloneFormModal: FC<Props> = ({ open, close, formId, formName, localizations = [] }: Props) => {
  const intl = useIntl();
  const formRef = useRef<FormikProps<Values>>(null);
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { cloneForm, isLoading: isLoadingForm, setOrganizationId } = useCloneForm(organizationId);

  const newFormName = useMemo(() => {
    if (Array.isArray(formName)) {
      return formName.map((item) => ({
        ...item,
        value: `${item.value} - copy`,
      }));
    } else {
      return formName + ' - copy';
    }
  }, [formName]);

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
    }
  }, [organizationId]);

  const FormCloneErrorMessages = Yup.object().shape({
    newFormName: cultureYup(
      Yup.string()
        .min(4, intl.formatMessage({ id: 'formSettings.errorMessage.short' }))
        .max(100, intl.formatMessage({ id: 'formSettings.errorMessage.long' }))
        .required(intl.formatMessage({ id: 'formSettings.errorMessage.required' })),
    ),
  });

  return (
    <ModalComponent
      closeSettings={() => close(false)}
      buttons={[
        {
          text: intl.formatMessage({ id: 'cancel' }),
          buttonVariant: 'secondary',
          onClick: () => close(false),
        },
        {
          text: intl.formatMessage({ id: 'confirm' }),
          buttonVariant: 'primary',
          loading: isLoadingForm,
          onClick: () => {
            formRef.current?.submitForm();
          },
        },
      ]}
      show={open}
      modalHeading={intl.formatMessage({
        id: 'formList.cloneFormModal.header',
      })}
      modalSize="lg"
    >
      <div className="container p-5">
        <Formik
          validationSchema={FormCloneErrorMessages}
          initialValues={{
            newFormName: newFormName,
          }}
          onSubmit={async (values: Values) => {
            if (values.newFormName.length > 0) {
              await cloneForm(formId, values.newFormName);
              close(true);
            }
          }}
          innerRef={formRef}
        >
          {() => (
            <Form>
              <FastField name={'newFormName'}>
                {({ meta, field, form }) => {
                  return (
                    <FieldWrapper
                      error={meta.error}
                      touched={meta.touched}
                      hiddenText={false}
                      textColor={undefined}
                      required={false}
                      label={intl.formatMessage({ id: 'formSettings.formInput.formName' })}
                      visible={true}
                      size={12}
                      name="value"
                    >
                      <CultureTextInput
                        localizations={localizations}
                        culture={intl.locale}
                        id="formName"
                        onChange={(val: CultureTextModel[]) => form.setFieldValue(field.name, val, true)}
                        onBlur={field.onBlur}
                        enabled={true}
                        placeholder={intl.formatMessage({ id: 'formSettings.formInput.placeholder' })}
                        value={field.value}
                        touched={meta.touched}
                        error={meta.error}
                      />
                    </FieldWrapper>
                  );
                }}
              </FastField>
            </Form>
          )}
        </Formik>
      </div>
    </ModalComponent>
  );
};

export default CloneFormModal;
